import type {
  AbstractPowerSyncDatabase,
  CrudTransaction,
  PowerSyncBackendConnector,
} from "@powersync/common";
import { classifyError, SyncErrorType } from "@stockpilot/powersync";

const TOKEN_EXPIRY_MINUTES = 30;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;

const DEFAULT_TOKEN_EXPIRY_MS = TOKEN_EXPIRY_MINUTES * SECONDS_PER_MINUTE * MS_PER_SECOND;
const DEFAULT_TOKEN_BUFFER_MS = SECONDS_PER_MINUTE * MS_PER_SECOND;
const MAX_OPERATIONS_PER_BATCH = 100;
const BASE64_GROUP = 4;
const JWT_PARTS_COUNT = 3;

/**
 * Decodes a JWT's `exp` claim (seconds) into epoch milliseconds. Returns `null`
 * when the token is not a decodable JWT or carries no numeric `exp`.
 *
 * Callers MUST treat `null` as "unknown", NEVER as "expired": an opaque/legacy
 * token we cannot decode is still served (resilient fallback). We only act when
 * we can POSITIVELY prove expiry.
 */
function decodeJwtExpMs(token: string): number | null {
  const parts = token.split(".");
  const [, payloadPart] = parts;
  if (parts.length !== JWT_PARTS_COUNT || !payloadPart) {
    return null;
  }
  try {
    const base64 = payloadPart.replaceAll("-", "+").replaceAll("_", "/");
    const padLength = (BASE64_GROUP - (base64.length % BASE64_GROUP)) % BASE64_GROUP;
    const padded = base64 + "=".repeat(padLength);
    const payload: unknown = JSON.parse(atob(padded));
    if (typeof payload === "object" && payload !== null && "exp" in payload) {
      const { exp } = payload;
      if (typeof exp === "number" && Number.isFinite(exp)) {
        return exp * MS_PER_SECOND;
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * True ONLY when we can positively prove the JWT is already expired (decoded
 * `exp` is at/before `now`). Undecodable or exp-less tokens return `false`
 * (unknown ≠ expired) so opaque tokens keep their resilient-fallback behavior.
 *
 * Serving an already-expired JWT to PowerSync makes the server reject the
 * connection with `JWT has expired` and reconnect in a loop, stranding the sync
 * in "Checking…" forever — so we refetch instead of handing out a dead token.
 */
function isJwtPositivelyExpired(token: string): boolean {
  const expMs = decodeJwtExpMs(token);
  return expMs !== null && expMs <= Date.now();
}

export type CrudOperation = {
  op: "PUT" | "PATCH" | "DELETE";
  table: string;
  transactionId?: string;
  id: string;
  opData?: Record<string, unknown>;
};

export interface TokenStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

export interface ConnectorConfig {
  endpoint: string;
  tokenExpiryMs?: number;
  fetchAuthToken: () => Promise<string | null>;
  uploadBatch: (operations: CrudOperation[]) => Promise<void>;
  storage: TokenStorage | null;
  onTokenFetchError?: (error: unknown) => void;
  onSyncSuccess?: (txCount: number) => void;
  /**
   * Namespaces the JWT cache keys so each account caches its own PowerSync
   * token independently. Without this, two accounts would clobber each other's
   * cached credentials in shared storage. Defaults to the legacy un-namespaced
   * keys for backward compatibility (single-account installs).
   */
  cacheNamespace?: string;
  /**
   * Multi-account upload gate (CC-42): `uploadBatch` posts through the app's
   * SHARED api client, whose credentials belong to the tab's CURRENTLY ACTIVE
   * account — not necessarily this connector's account. If this connector's
   * account is NOT the active API identity (e.g. its queued CRUD flushes right
   * after an account switch, or the initial-sync upload throttle delays the
   * flush past a switch), the server would authenticate the batch as the OTHER
   * account and silently drop/misattribute the ops — permanent local-first data
   * loss (the op completes, the row later reverts to the server value).
   *
   * When this returns `false`, `uploadData` DEFERS: it returns without reading
   * or completing any transaction, so the ops stay queued and upload the next
   * time this account is active (the account-activation path re-triggers the
   * upload queue). Omit for single-account installs (no gate).
   */
  canUploadNow?: () => boolean;
}

export class BasePowerSyncConnector implements PowerSyncBackendConnector {
  private readonly config: ConnectorConfig;
  private static readonly TOKEN_CACHE_KEY = "powersync_jwt_cache";
  private static readonly EXPIRES_CACHE_KEY = "powersync_jwt_expires";

  constructor(config: ConnectorConfig) {
    this.config = config;
  }

  private get tokenCacheKey(): string {
    const { cacheNamespace } = this.config;
    return cacheNamespace
      ? `${BasePowerSyncConnector.TOKEN_CACHE_KEY}:${cacheNamespace}`
      : BasePowerSyncConnector.TOKEN_CACHE_KEY;
  }

  private get expiresCacheKey(): string {
    const { cacheNamespace } = this.config;
    return cacheNamespace
      ? `${BasePowerSyncConnector.EXPIRES_CACHE_KEY}:${cacheNamespace}`
      : BasePowerSyncConnector.EXPIRES_CACHE_KEY;
  }

  async clearTokenCache(): Promise<void> {
    const { storage } = this.config;
    if (storage) {
      await storage.setItem(this.tokenCacheKey, "");
      await storage.setItem(this.expiresCacheKey, "");
    }
  }
  async fetchCredentials(): Promise<{
    endpoint: string;
    token: string;
    expiresAt?: Date;
  }> {
    const { endpoint, storage, tokenExpiryMs = DEFAULT_TOKEN_EXPIRY_MS } = this.config;
    const { tokenCacheKey, expiresCacheKey } = this;

    // 1. Validate cached token
    if (storage) {
      const cachedToken = await storage.getItem(tokenCacheKey);
      const cachedExpires = await storage.getItem(expiresCacheKey);

      if (cachedToken && cachedExpires) {
        const expiresAt = new Date(cachedExpires);
        const TOKEN_EXPIRY_BUFFER_MS = DEFAULT_TOKEN_BUFFER_MS;
        // Validate BOTH the client-side bookkeeping (`expiresAt` + buffer) AND
        // the REAL JWT `exp`. A token can be expired server-side even if our
        // client clock/bookkeeping disagrees; never serve a positively-expired
        // JWT from cache (it would 401-loop) — fall through to refetch instead.
        const clientStillValid = expiresAt.getTime() > Date.now() + TOKEN_EXPIRY_BUFFER_MS;
        if (clientStillValid && !isJwtPositivelyExpired(cachedToken)) {
          return { endpoint, token: cachedToken, expiresAt };
        }
      }
    }

    try {
      // 2. Fetch new token
      const token = await this.config.fetchAuthToken();

      if (!token) {
        throw new Error("Failed to fetch valid token from auth provider.");
      }

      const newExpiresAt = new Date(Date.now() + tokenExpiryMs);

      // 3. Persist
      if (storage) {
        await storage.setItem(tokenCacheKey, token);
        await storage.setItem(expiresCacheKey, newExpiresAt.toISOString());
      }

      return {
        endpoint,
        token,
        expiresAt: newExpiresAt,
      };
    } catch (error: unknown) {
      if (storage) {
        const cachedToken = await storage.getItem(tokenCacheKey);
        const cachedExpires = await storage.getItem(expiresCacheKey);

        // Serve the last-known token to ride out a TRANSIENT fetch failure — but
        // ONLY while it is not positively expired. A decoded-expired JWT served
        // here just makes PowerSync 401 and reconnect forever ("JWT has
        // expired"), pinning the sync in "Checking…". If the cached JWT is
        // provably expired we let the error propagate so the connection surfaces
        // as failed (→ reconnecting/backoff) instead of looping on a dead token.
        if (cachedToken && cachedExpires && !isJwtPositivelyExpired(cachedToken)) {
          const staleExpiresAt = new Date(cachedExpires);
          if (this.config.onTokenFetchError) {
            this.config.onTokenFetchError(error);
          }
          return { endpoint, token: cachedToken, expiresAt: staleExpiresAt };
        }
      }

      const errorMessage = error instanceof Error ? error.message : "Error";
      throw new Error(`[PowerSync] Failed to obtain credentials: ${errorMessage}`, {
        cause: error,
      });
    }
  }

  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    // CC-42: never upload this account's ops under ANOTHER account's API
    // credentials — defer (ops stay queued) until this account is active again.
    if (this.config.canUploadNow && !this.config.canUploadNow()) {
      console.warn(
        "[PowerSync] Upload deferred: this connector's account is not the active API identity.",
      );
      return;
    }

    let totalOperationsCount = 0;

    const transactions: CrudTransaction[] = [];
    // CC-25: iterate DISTINCT pending transactions. `getNextCrudTransaction()`
    // always returns the OLDEST transaction that has not been `complete()`d yet —
    // so a loop that collects several transactions BEFORE completing any kept
    // re-yielding the SAME transaction until the op ceiling was hit, uploading
    // dozens of duplicate copies of one transaction in a single batch (observed:
    // the same 3-op PUT sent 34× to reach the 100-op cap). `getCrudTransactions()`
    // yields each pending transaction exactly once; completing the LAST one marks
    // it AND every prior transaction from the iterator as done.
    for await (const transaction of database.getCrudTransactions()) {
      const txOpsCount = transaction.crud.length;
      if (totalOperationsCount + txOpsCount > MAX_OPERATIONS_PER_BATCH && transactions.length > 0) {
        break;
      }

      transactions.push(transaction);
      totalOperationsCount += txOpsCount;

      if (totalOperationsCount >= MAX_OPERATIONS_PER_BATCH) {
        break;
      }
    }

    if (transactions.length === 0) {
      return;
    }

    const lastTransaction = transactions.at(-1);
    try {
      const operations: CrudOperation[] = transactions.flatMap((tx) =>
        tx.crud.map<CrudOperation>((op) => ({
          op: op.op as CrudOperation["op"],
          table: op.table,
          id: op.id,
          opData: op.opData,
          // `CrudTransaction.transactionId` is `number | undefined`. A
          // `String(undefined)` sends the literal "undefined", and then ALL
          // transactions without an id collapse into the same server group and
          // are applied as one: extra blocking and, if one fails, writes the
          // user never grouped are discarded.
          transactionId: tx.transactionId == null ? undefined : String(tx.transactionId),
        })),
      );

      await this.config.uploadBatch(operations);

      // Completing the last transaction of the iterator marks it and ALL prior
      // ones as done — a single call, not one per transaction.
      await lastTransaction?.complete();

      if (this.config.onSyncSuccess) {
        this.config.onSyncSuccess(transactions.length);
      }
    } catch (error) {
      const [firstTransaction] = transactions;
      if (firstTransaction) {
        await this.handleError(error, firstTransaction);
      }
    }
  }

  private async handleError(error: unknown, transaction: CrudTransaction): Promise<void> {
    const errorType = classifyError(error);

    switch (errorType) {
      case SyncErrorType.RETRYABLE: {
        throw error;
      }
      case SyncErrorType.CONFLICT:
      case SyncErrorType.VALIDATION: {
        await transaction.complete();
        break;
      }
      case SyncErrorType.PERMISSION:
      case SyncErrorType.UNKNOWN: {
        throw error;
      }
      default: {
        throw error;
      }
    }
  }
}
